/**
 * FeedbackDialog — modal "Send feedback" entry for pages whose composer
 * has no feedback mode (NewChat home). DesignView keeps its composer-based
 * flow (feedbackMode); both funnel into the same submitFeedbackBundle S3
 * layout, so the admin triage inbox sees them identically.
 *
 * Styling matches ConfirmDialog's modal-* classes so it renders native to
 * the app shell in both themes.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { submitFeedback as submitFeedbackBundle } from '../services/feedbackUpload';
import { getProjectName } from './ProjectTree';
import { listDesigns, recordPortalFeedback } from '../services/api';

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // mirror feedbackUpload's cap

export default function FeedbackDialog({ open, user, designContext, designs = null, onClose }) {
  const [text, setText] = useState('');
  const [projectId, setProjectId] = useState('');
  const [allDesigns, setAllDesigns] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setText('');
      setFiles([]);
      setError('');
      setBusy(false);
      setSavedId('');
      // Preselect the project when the dialog opens inside a design.
      setProjectId(designContext?.projectId || designContext?.designId || '');
      // Autocomplete options come from the parent when it already has the
      // designs list; only fetch as a fallback.
      const email = user?.email;
      if (email && !designs) {
        let cancelled = false;
        listDesigns(email)
          .then((data) => { if (!cancelled) setAllDesigns(data?.designs || []); })
          .catch(() => {});
        return () => { cancelled = true; };
      }
    }
  }, [open, user?.email, designContext, designs]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Project options — same grouping rules as ProjectTree: dedupe rows to
  // the latest version per designId, group by projectId (legacy rows fall
  // back to their own designId), label from the shell row's designName.
  // Customer name rides along from the shell (or any row that has one)
  // so feedback still lands attributed to a customer without its own field.
  const sourceDesigns = designs || allDesigns;
  const projects = useMemo(() => {
    const latestByDesignId = new Map();
    for (const d of sourceDesigns || []) {
      const did = d.designId || d.workflowId || d.id;
      if (!did) continue;
      const existing = latestByDesignId.get(did);
      if (!existing || (d.version || 0) > (existing.version || 0)) {
        latestByDesignId.set(did, d);
      }
    }
    const groupsById = new Map();
    for (const d of latestByDesignId.values()) {
      const pid = d.projectId || d.designId || d.workflowId;
      if (!pid) continue;
      if (!groupsById.has(pid)) groupsById.set(pid, []);
      groupsById.get(pid).push(d);
    }
    const out = [];
    for (const [pid, rows] of groupsById.entries()) {
      const shell = rows.find((r) => r.status === 'project_shell');
      const name = (shell && (shell.designName || shell.name)) || getProjectName(rows[0]) || 'AJO';
      const customerName = (
        (shell?.customerName || '').trim()
        || (rows.find((r) => (r.customerName || '').trim())?.customerName || '').trim()
      );
      const rowTs = (r) => {
        const v = Date.parse(r.updatedAt || r.lastModified || r.createdAt || '');
        return Number.isFinite(v) ? v : 0;
      };
      out.push({
        projectId: pid,
        projectName: name,
        customerName,
        designId: (shell || rows[0])?.designId || pid,
        lastTs: Math.max(0, ...rows.map(rowTs)),
      });
    }
    out.sort((a, b) => b.lastTs - a.lastTs);
    return out;
  }, [sourceDesigns]);

  if (!open) return null;

  const addFiles = (list) => {
    const incoming = Array.from(list || []).filter((f) => {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        setError(`"${f.name}" is over 25 MB — attach a smaller file.`);
        return false;
      }
      return true;
    });
    if (incoming.length) setFiles((prev) => [...prev, ...incoming]);
  };

  const submit = async () => {
    if (busy) return;
    const description = text.trim();
    if (!description && files.length === 0) {
      setError('Add a short description or attach a screenshot first.');
      return;
    }
    const project = projects.find((p) => p.projectId === projectId);
    if (!project) {
      setError('Select the project this feedback is about.');
      return;
    }
    const email = user?.email;
    if (!email) {
      setError('Sign-in required to submit feedback.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await submitFeedbackBundle(files, {
        consultantId: email,
        consultantEmail: email,
        pageUrl: window.location.href,
        description,
        // Customer derives from the selected project's row — no separate field.
        customerName: project.customerName || '',
        designId: designContext?.designId || project.designId || '',
        extra: { projectId: project.projectId, projectName: project.projectName },
      });
      // Mirror into the shared portal metrics tables — fire-and-forget.
      recordPortalFeedback({
        customerName: project.customerName || 'Unknown',
        type: 'feedback',
        feedbackText: description,
      }).catch(() => {});
      setSavedId(result.feedbackId);
    } catch (e) {
      setError(e?.message || 'Could not save feedback — please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="modal-card feedback-dialog" role="dialog" aria-modal="true" aria-label="Send feedback">
        <div className="modal-header">
          <h2>Send feedback</h2>
        </div>
        {savedId ? (
          <>
            <div className="modal-body">
              <div style={{ fontSize: 13.5, color: 'var(--text-primary)' }} role="status">
                ✅ Feedback saved — thank you! Reference <code>{savedId}</code>.
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="modal-btn modal-btn-primary" onClick={() => onClose?.()}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                Found a bug, missing feature, or rough edge? Describe it below — screenshots help.
              </div>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }} htmlFor="feedback-project">
                Project
              </label>
              <select
                id="feedback-project"
                className="modal-input"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                disabled={busy}
                style={{ width: '100%', boxSizing: 'border-box', marginBottom: 12 }}
              >
                <option value="">Select a project…</option>
                {projects.map((p) => (
                  <option key={p.projectId} value={p.projectId}>{p.projectName}</option>
                ))}
              </select>
              <textarea
                rows={5}
                maxLength={4000}
                value={text}
                autoFocus
                onChange={(e) => setText(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }}
                placeholder="What happened, and what did you expect instead?"
              />
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="modal-btn modal-btn-ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={busy}
                >
                  📎 Attach files
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    addFiles(e.target.files);
                    e.target.value = '';
                  }}
                />
                {files.map((f, i) => (
                  <span
                    key={`${f.name}-${i}`}
                    style={{
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      background: 'var(--surface-2)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      padding: '2px 8px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {f.name}
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      disabled={busy}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 0 }}
                      aria-label={`Remove ${f.name}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              {error && (
                <div style={{ color: 'var(--danger, #d32f2f)', fontSize: 12.5, marginTop: 8 }} role="alert">
                  {error}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="modal-btn modal-btn-ghost" onClick={() => onClose?.()} disabled={busy}>
                Cancel
              </button>
              <button type="button" className="modal-btn modal-btn-primary" onClick={submit} disabled={busy}>
                {busy ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
